import { ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { LocationStrategy } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { registerLocaleData } from '@angular/common';
import localeEsCo from '@angular/common/locales/es-CO';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorNormalizerInterceptor } from './core/interceptors/error-normalizer.interceptor';
import { sedeInterceptor } from './core/interceptors/sede.interceptor';
import { sessionExpiredInterceptor } from './core/interceptors/session-expired.interceptor';
import { operationalTimeoutInterceptor } from './core/interceptors/operational-timeout.interceptor';
import { AuthService } from './core/services/auth.service';
import { isCustomDomainHost } from './core/custom-domain/custom-domain.util';
import { CustomDomainContextService } from './core/custom-domain/custom-domain-context.service';
import { CustomDomainLocationStrategy } from './core/custom-domain/custom-domain-location.strategy';
import { PublicRestaurantService } from './core/services/public-restaurant.service';
import { CacheInvalidationBridge } from './core/services/cache-invalidation-bridge';

const DEFAULT_LANG = 'es';

// En dominio custom usamos las rutas normales (con :slug) + un LocationStrategy que
// hace el slug un prefijo transparente: el router ve /{slug}/... (toda la navegación
// existente funciona) y el navegador muestra la URL sin el slug.
const isCustomDomain = isCustomDomainHost(window.location.hostname);

// Resuelve el dominio custom -> slug antes de que el router procese las rutas.
function initializeCustomDomain(
  ctx: CustomDomainContextService,
  publicSvc: PublicRestaurantService
) {
  return async () => {
    const host = window.location.hostname;
    if (!isCustomDomainHost(host)) {
      return;
    }
    try {
      const res = await firstValueFrom(publicSvc.resolveByHost(host));
      if (res?.data?.slug) {
        ctx.setResolved(res.data.slug, res.data.name ?? null);
      } else {
        ctx.setUnresolved();
      }
    } catch {
      // 404 o error de red -> dominio custom sin restaurante -> 404 en la app.
      ctx.setUnresolved();
    }
  };
}

function initializeApp(authService: AuthService) {
  return () => authService.loadUserFromStorage();
}

// Instancia el bridge SignalR -> invalidacion de cache al arranque. Su constructor
// registra los effects que escuchan los eventos realtime; sin instanciarlo, la cache
// no se invalidaria por eventos de otros usuarios. No bloquea el arranque (no-op init).
function initializeCacheInvalidation(_bridge: CacheInvalidationBridge) {
  return () => undefined;
}

function initializeTranslations(translate: TranslateService) {
  return () => {
    translate.setFallbackLang(DEFAULT_LANG);
    return firstValueFrom(translate.use(DEFAULT_LANG));
  };
}

// Registrar locale español de Colombia
registerLocaleData(localeEsCo);

// Sakai-inspired preset with blue (#3B82F6) as primary color
const SakaiPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    ...(isCustomDomain ? [{ provide: LocationStrategy, useClass: CustomDomainLocationStrategy }] : []),
    provideHttpClient(withInterceptors([authInterceptor, sedeInterceptor, errorNormalizerInterceptor, sessionExpiredInterceptor, operationalTimeoutInterceptor])),
    MessageService,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: SakaiPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.app-dark',
          cssLayer: false
        }
      },
      overlayOptions: {
        appendTo: 'body'
      }
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        fallbackLang: DEFAULT_LANG
      })
    ),
    // Cache-busting del JSON de traducciones: cada bootstrap de la app genera un
    // timestamp distinto, forzando al navegador a traer la version fresca. Evita
    // que mobile Safari/Chrome mantenga copias viejas de es.json y muestre claves
    // crudas tipo "owner.dashboard.title" cuando hay deploy con nuevas claves.
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: `.json?v=${Date.now()}`
    }),
    { provide: LOCALE_ID, useValue: 'es-CO' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslations,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AuthService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCustomDomain,
      deps: [CustomDomainContextService, PublicRestaurantService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCacheInvalidation,
      deps: [CacheInvalidationBridge],
      multi: true
    }
  ]
};
